import getKeyForSuggestion from 'owa-3s-local/lib/actions/getKeyForSuggestion';
import { InitializeCacheState } from './initializeCache';
import datapoints from '../datapoints';
import { findPeople } from '../services/findPeople';
import substrateSuggestion from '../services/substrateSuggestion';
import { RecipientCacheSource } from '../store/schema/RecipientCacheSource';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import createFindPeopleFeedbackManager from 'owa-controls-findpeople-feedback-manager/lib/actions/createFindPeopleFeedbackManager';
import transactionFailed from 'owa-controls-findpeople-feedback-manager/lib/actions/transactionFailed';
import { OwaDate } from 'owa-datetime';
import { now } from 'owa-datetime';
import setFallbackToFindPeople from 'owa-recipient-suggestions/lib/actions/setFallbackToFindPeople';
import convertSuggestionToPersonaType from 'owa-recipient-suggestions/lib/util/convertSuggestionToPersonaType';
import getTransactionIdFromResponse from 'owa-recipient-common/lib/utils/getTransactionIdFromResponse';
import { SubstrateSearchSuggestionsResponse } from 'owa-search-service';
import PersonaType from 'owa-service/lib/contract/PersonaType';
import { trace } from 'owa-trace';
import { isTrieCacheEnabled } from 'owa-substrate-people-suggestions/lib/utils/isTrieCacheEnabled';
import { lazyInitializeSuggestionTrie } from 'owa-3s-local';

const CONTEXT = {
    Action: 'Type Down',
};

export default createLegacyActionWithDatapoint(
    'tryInitializeCacheWithSource',
    datapoints.RecipientCacheInitialization
)(async function tryInitializeCacheWithSource(
    source: RecipientCacheSource,
    fallback: boolean /* used for datapoints, used to track whether we had to use the fallback method to populate the cache */,
    state?: InitializeCacheState
): Promise<void> {
    const transactionStart = now();
    let transactionId = '';
    try {
        let cacheResults: PersonaType[];
        switch (source) {
            case RecipientCacheSource.SubstrateSuggestions:
                const substrateSuggestionResult = await substrateSuggestion();
                cacheResults = convert3SResultsToPersonaType(substrateSuggestionResult);
                transactionId = getTransactionIdFromResponse(substrateSuggestionResult);
                break;
            case RecipientCacheSource.FindPeople:
                const findPeopleResult = await findPeople();
                cacheResults = findPeopleResult.ResultSet;
                transactionId = getTransactionIdFromResponse(findPeopleResult);
                break;
        }

        if (fallback) {
            setFallbackToFindPeople(true);
        }

        processCacheCallResults(cacheResults, state, source, fallback);
    } catch (error) {
        trace.warn(error.message);
        addTransactionFailed(transactionStart, transactionId);
    }
});

function processCacheCallResults(
    cacheResults: PersonaType[],
    state: InitializeCacheState,
    source: RecipientCacheSource,
    fallback: boolean
): void {
    if (cacheResults && cacheResults.length > 0) {
        cacheResults.forEach(x => {
            let suggestionKey = getKeyForSuggestion(x);
            if (suggestionKey) {
                state.store.allKeys[suggestionKey] = true;
            }
        });
        state.store.recipientCache = cacheResults;

        if (
            isTrieCacheEnabled() &&
            source == RecipientCacheSource.SubstrateSuggestions &&
            !fallback
        ) {
            lazyInitializeSuggestionTrie.importAndExecute(cacheResults);
        }
    }
}

function convert3SResultsToPersonaType(resp: SubstrateSearchSuggestionsResponse): PersonaType[] {
    let personaTypeSet: PersonaType[] = [];

    if (!resp || !resp.Groups) {
        trace.warn('Response groups should not be null');
    } else {
        const transactionId = resp.Instrumentation?.TraceId;
        for (const group of resp.Groups) {
            if (group.Type === 'People') {
                personaTypeSet = group.Suggestions.map(suggestion => {
                    return convertSuggestionToPersonaType(suggestion, transactionId);
                });
                break;
            }
        }
    }

    return personaTypeSet;
}

function addTransactionFailed(transactionStartTime: OwaDate, transactionId: string): void {
    transactionFailed(
        createFindPeopleFeedbackManager(),
        '' /*queryString*/,
        transactionId,
        transactionStartTime,
        CONTEXT
    );
}
